import { Close } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { Box,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions } from '@mui/material';
import ViewColumn from '@mui/icons-material/ViewColumn';
import TableRows from '@mui/icons-material/TableRows';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { BULK_IMPORT_BASE_COLS, validateSystemsEligable } from 'relevant-shared/misc/bulkImport';
import { createCsv } from 'relevant-shared/misc/misc';
import { PublisherNode, SspPlacementNode } from 'relevant-shared/objects/pubTree';
import classes from '../../../api/classes';
import { Link } from '../../../components/Link/Link';
import BrowserUtils from '../../../lib/browserUtils';
import RadioButtonGroup from '../../../components/RadioButtonGroup';
import CsvImporter from './CsvImporter';
import CsvUploader from './CsvUploader';
import { ActionButton } from '../../../components/ActionButton/ActionButton';

const {
	BaseAdserver, GlobalSettingsObject, Publisher, BaseSsp,
} = classes;

export const exportPublisherStructureV2 = async () => {
	const [publishers, ssps, adservers, globalSettings] = await Promise.all([
		Publisher.list(),
		BaseSsp.list(),
		BaseAdserver.list(),
		GlobalSettingsObject.listOne(),
	]);
	validateSystemsEligable({ ssps, adservers, publishers });
	const pubNodes = publishers.map((pub) => new PublisherNode(pub, {
		ssps,
		adservers,
		globalSettings,
	}));
	const placs = pubNodes.map((n) => n.byType.PlacementNode).flat();
	const res = [];
	placs.forEach((plac) => {
		const {
			adserver, adserverSettings, placementType,
		} = plac;
		const sspPlacNodes = plac.children.filter((node) => node.ssp && node instanceof SspPlacementNode);
		let placId = '';
		if (adserver) {
			const { useSspPlacementsForIds } = adserver;
			const { correspondingSspId, placIdField } = adserver;
			placId = adserverSettings?.[placIdField];
			if (useSspPlacementsForIds && correspondingSspId) {
				placId = _.map(_.filter(sspPlacNodes, { sspId: correspondingSspId }), 'sspPlacementId').filter((s) => s).join('\n')
					|| placId;
			}
		}
		const rowBase = _.mapValues({
			publisherName: plac.publisherNode.name,
			site: plac.parentNode.name,
			placementName: plac.name,
			placementType: placementType?.name,
			adserver: adserver?.name,
			..._.zipObject(_.map(ssps, 'name'), Array(ssps.length).fill('')),
		}, (v) => v || '');
		const perSsp = Object.values(_.groupBy(sspPlacNodes, 'sspId'));
		const rowCount = _.maxBy(perSsp, 'length')?.length || 0;
		for (let i = 0; i < rowCount; i += 1) {
			const row = { ...rowBase, adserverPlacement: i ? '' : placId || '' };
			perSsp.forEach((arr) => {
				const { ssp, sspPlacementId } = arr[i] || {};
				if (ssp) {
					row[ssp.name] = sspPlacementId;
				}
			});
			res.push(row);
		}
	});
	const csv = createCsv(res, {
		...BULK_IMPORT_BASE_COLS,
		..._.zipObject(_.map(ssps, 'name'), _.map(ssps, 'name')),
	});
	BrowserUtils.downloadTextFile(csv, 'Publisher structure.csv');
};

export function CsvImportManager({ onImportComplete, onError, onClose }) {
	const [uploadedCsv, setUploadedCsv] = useState();

	const [replaceModes, setReplaceModes] = useState({});
	const replaceModesRef = useRef(replaceModes);

	useEffect(() => {
		replaceModesRef.current = replaceModes;
	}, [replaceModes]);

	const [activeDialog, setActiveDialog] = useState(null);
	const [tempSelectedMode, setTempSelectedMode] = useState(null);
	const uploaderRef = useRef();

	const modeLabels = {
		none: {
			displayLabel: 'Configure',
			label: 'None',
			desc: 'Adds a new SSP entry if the ID differs from existing ones. May result in duplicates.',
		},
		replaceFirst: {
			label: 'Replace First',
			desc: 'Replaces the first matching SSP entry with the new ID.',
		},
		replaceAll: {
			label: 'Replace All',
			desc: 'Replaces the first matching SSP entry and removes any additional entries.',
		},
	};

	const modeItems = Object.entries(modeLabels).map(([value, { label, desc }]) => ({
		value,
		name: label,
		description: desc,
	}));

	const setMode = (key, mode) => {
		setReplaceModes((prev) => ({
			...prev,
			[key]: mode,
		}));
	};

	const headers = () => Object.fromEntries(
		Object.entries(uploadedCsv.columns).map(([key, label]) => {
			const isSsp = uploadedCsv.sspsInCsv.includes(label);
			return [
				key,
				isSsp
					? {
						label,
						isSsp,
						renderHeader: () => {
							const modeKey = replaceModes[label] || 'none';
							const mode = modeLabels[modeKey];
							const buttonLabel = mode.displayLabel || mode.label;

							return (
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<Button
										variant="outlined"
										size="small"
										onClick={() => {
											setActiveDialog(key);
											setTempSelectedMode(modeKey);
										}}
									>
										{buttonLabel}
									</Button>
									<span style={{ marginLeft: 4 }}>{label}</span>
								</Box>
							);
						},
					}
					: { label },
			];
		}),
	);

	const revalidateCsv = () => {
		if (uploaderRef.current?.revalidateCsv && uploadedCsv?.file) {
			uploaderRef.current.revalidateCsv();
		}
	};

	return (
		<>
			{activeDialog && (
				<Dialog
					open
					onClose={() => setActiveDialog(null)}
					fullWidth
				>
					<DialogTitle>
						Import Strategy for {uploadedCsv.columns[activeDialog]}
					</DialogTitle>
					<DialogContent dividers>
						<RadioButtonGroup
							name={`replace-mode-${activeDialog}`}
							items={modeItems}
							selected={tempSelectedMode}
							onChange={(e) => setTempSelectedMode(e.target.value)}
						/>
					</DialogContent>
					<DialogActions>
						<ActionButton
							label="Cancel"
							variant="text"
							onClick={() => {
								setActiveDialog(null);
							}} />
						<ActionButton
							label="Apply to column"
							icon={<TableRows />}
							color="primary"
							onClick={() => {
								setMode(activeDialog, tempSelectedMode);
								revalidateCsv();
								setActiveDialog(null);
							}}
						/>

						<ActionButton
							label="Apply to all columns"
							icon={<ViewColumn />}
							variant="outlined"
							onClick={() => {
								setReplaceModes((prev) => {
									const updated = { ...prev };
									Object.entries(uploadedCsv.columns).forEach(([key, label]) => {
										if (uploadedCsv.sspsInCsv.includes(label)) {
											updated[key] = tempSelectedMode;
										}
									});
									return updated;
								});
								revalidateCsv();
								setActiveDialog(null);
							}}
						/>
					</DialogActions>
				</Dialog>
			)}
			<div className="flex *:flex-1 justify-center">
				<div />
				<div className="w-full">
					<CsvUploader
						ref={uploaderRef}
						getReplaceModes={() => replaceModesRef.current}
						onUploadFile={(csv, file, err) => {
							if (err) {
								onError(err.message);
							} else {
								setUploadedCsv({ ...csv, file });
							}
						}}
						className={uploadedCsv ? 'hidden' : ''}
					/>
					{uploadedCsv && (
						<CsvImporter
							data={uploadedCsv.rows}
							columns={headers()}
							getReplaceModes={() => replaceModes}
							dataIssues={uploadedCsv.issues.dataIssues}
							missingSspErrors={uploadedCsv.issues.missingSspErrors}
							expectedResult={uploadedCsv.expectedResult}
							file={uploadedCsv.file}
							onCancel={() => setUploadedCsv(null)}
							onDone={(success) => {
								setUploadedCsv(null);
								onImportComplete(success);
							}}
							onIgnore={(issuesToIgnore) => {
								const { issues: { dataIssues } } = uploadedCsv;
								const updatedDataIssues = { ...dataIssues };

								issuesToIgnore.forEach((issue) => {
									const index = Number(issue.row) - 1;
									updatedDataIssues[index][issue.key] = updatedDataIssues[index][issue.key]
										.filter(({ detailedMessage }) => (
											detailedMessage !== issue.detailedDescription
										));
								});

								setUploadedCsv({
									...uploadedCsv,
									issues: {
										...uploadedCsv.issues,
										dataIssues: updatedDataIssues,
									},
								});
							}}
						/>
					)}
				</div>
				<div>
					{ !uploadedCsv && (
						<div className="flex flex-col justify-between items-end h-full">
							<button className="text-grey-250 hover:text-grey-800" onClick={onClose}><Close label="Close" /></button>
							<div className="flex flex-col items-end">
								<Link
									className="no-underline py-1 font-normal!"
									href="/src/assets/example_csv_import_file.csv"
									isDownloadLink
								>
									<DownloadIcon /><span className="ml-1">Download example CSV</span>
								</Link>
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

CsvImportManager.propTypes = {
	onImportComplete: PropTypes.func.isRequired,
	onError: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};
